import { StyleSheet, View } from 'react-native';
import type { ReactNode } from 'react';

import { Text } from '@/components/ui';
import { fluidFontSize, fluidLineHeight, palette, useTheme } from '@/theme';

export type SectionHeadProps = {
  idx: string;
  title: string;
  italic?: string;
  tail?: string;
  sub?: ReactNode;
  invert?: boolean;
};

/** Section header matching design: 180px eyebrow column + content right. */
export function SectionHead({ idx, title, italic, tail, sub, invert }: SectionHeadProps) {
  const theme = useTheme();
  const idxColor = invert ? 'rgba(245,240,255,0.6)' : theme.colors.fgMute;
  const titleColor = invert ? palette.paper : theme.colors.fg;
  const italicColor = invert ? palette.emberSoft : theme.colors.accent;
  const subColor = invert ? 'rgba(245,240,255,0.78)' : theme.colors.fgSoft;

  return (
    <View style={styles.row}>
      <View style={styles.idxCol}>
        <Text style={[styles.idx, { color: idxColor }]}>{idx}</Text>
      </View>
      <View style={styles.body}>
        <Text
          style={{
            fontFamily: theme.fontFamily.display,
            fontSize: fluidFontSize(36, 5.2, 56) as any,
            lineHeight: fluidLineHeight(1.05, 58) as any,
            color: titleColor,
            letterSpacing: -1,
          }}
        >
          {title}
          {italic ? (
            <>
              {' '}
              <Text
                italic
                style={{
                  fontFamily: theme.fontFamily.displayItalic,
                  fontStyle: 'italic',
                  color: italicColor,
                  fontSize: fluidFontSize(36, 5.2, 56) as any,
                }}
              >
                {italic}
              </Text>
            </>
          ) : null}
          {tail ? <Text style={{ color: titleColor }}> {tail}</Text> : null}
        </Text>
        {sub ? (
          typeof sub === 'string' ? (
            <Text
              style={{ color: subColor, fontSize: 17, lineHeight: 26, marginTop: 14, maxWidth: 600 }}
            >
              {sub}
            </Text>
          ) : (
            <View style={{ marginTop: 14 }}>{sub}</View>
          )
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
    marginBottom: 56,
    alignItems: 'flex-start',
  },
  idxCol: {
    flexBasis: 180,
    flexGrow: 0,
    paddingTop: 8,
  },
  idx: {
    fontFamily: 'JetBrainsMono_500Medium',
    fontSize: 11,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
  body: {
    flex: 1,
    minWidth: 280,
  },
});
